import { useIntl } from 'react-intl';
import { ResponsiveLine } from '@nivo/line';
import { Typography } from 'antd';
import { currencyFormatter, metricPrefixFormatter } from '@/utils/number';
import { ContentWrapper, EllipsisButton } from '@/components/_common';
import { useEffect } from 'react';
import { useChartTheme, useConvertChartData, useDisclosure } from '@/hooks';
import moment from 'moment';
import { useChartCostFixedVariable } from '@/core/domain/workorder/ManagementCharts/ManagementChart.hooks';
import { DateTimeFormat } from '@/utils/moment';
import {
  CostFixedVariableFormValues,
  CostFixedVariableParams,
} from '@/core/domain/workorder/ManagementCharts/ManagementChart.types';
import ChartDefaultFilter from '../ChartDefaultFilter';

const { Title, Paragraph } = Typography;

const defaultStartDate = moment().subtract(5, 'months').startOf('day');
const defaultEndDate = moment().endOf('day');

function FinancialCostFixed() {
  const intl = useIntl();
  const chartTheme = useChartTheme();
  const [modalVisible, modalVisibleAction] = useDisclosure();

  const chartCostFiexed = useChartCostFixedVariable();

  const chartData = useConvertChartData(chartCostFiexed.data?.chartData);

  useEffect(() => {
    chartCostFiexed.mutateAsync({
      startDate: defaultStartDate.format(DateTimeFormat),
      endDate: defaultEndDate.format(DateTimeFormat),
    });
  }, []);

  const handleOk = async (values: CostFixedVariableFormValues) => {
    try {
      const obj: CostFixedVariableParams = {
        ...values,
        startDate: values.startDate.format(DateTimeFormat),
        endDate: values.endDate.format(DateTimeFormat),
      };

      await chartCostFiexed.mutateAsync(obj);

      modalVisibleAction.close();
    } catch {}
  };

  return (
    <>
      <ContentWrapper
        style={{ height: '430px', padding: '8px' }}
        extra={<EllipsisButton onClick={modalVisibleAction.open} />}
      >
        <Title level={5} style={{ textAlign: 'center', marginBottom: '4px' }}>
          Custo (Fixo x Variável)
        </Title>

        <Paragraph style={{ textAlign: 'center', margin: '4px 0 4px 0' }}>
          {`Período: ${moment(chartCostFiexed.variables?.startDate).format(
            'DD/MM/YYYY',
          )} a ${moment(chartCostFiexed.variables?.endDate).format(
            'DD/MM/YYYY',
          )}`}
        </Paragraph>

        <ResponsiveLine
          data={chartData ? chartData : []}
          enableSlices="x"
          margin={{ top: 15, right: 25, bottom: 90, left: 70 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 0,
            max: 'auto',
            reverse: false,
          }}
          yFormat={(value) => `${currencyFormatter(value)}`}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 7,
            tickPadding: 10,
            tickRotation: 0,
            legendOffset: 36,
            legendPosition: 'middle',
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            tickValues: 5,
            legend: intl.formatMessage({
              id: 'dashboard.averageTicketReport.averageTicketPartsAndServices',
            }),
            format: (value) => `${metricPrefixFormatter(+value)}`,
            legendOffset: -50,
            legendPosition: 'middle',
          }}
          enableGridX={false}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          useMesh
          theme={chartTheme}
        />
      </ContentWrapper>

      <ChartDefaultFilter
        open={modalVisible}
        onOk={handleOk}
        onCancel={modalVisibleAction.close}
        title="Filtros do gráfico de Fluxo de Caixa"
      />
    </>
  );
}

export default FinancialCostFixed;
